import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { useSubject } from '../context/SubjectContext'
import { supabase } from '../lib/supabase'

export function useTaxProfile() {
  const { user } = useAuth()
  const { activeSubject, activeTaxYear, reload: reloadSubjects, ensureSelfSubject } = useSubject()

  const [taxProfile,     setTaxProfile]     = useState(null)  // tax_profiles row
  const [yearProfile,    setYearProfile]    = useState(null)  // tax_year_profiles row
  const [loading,        setLoading]        = useState(true)
  const [saving,         setSaving]         = useState(false)

  const subjectId = activeSubject?.id || null

  useEffect(() => {
    if (user && subjectId) loadProfiles()
    else if (user && !subjectId) setLoading(false)
  }, [user, subjectId, activeTaxYear]) // eslint-disable-line

  const loadProfiles = useCallback(async () => {
    if (!user || !subjectId) return
    console.log('[useTaxProfile] loadProfiles fired', { subjectId, activeTaxYear })
    setLoading(true)
    setTaxProfile(null)
    setYearProfile(null)

    const profilePromise = supabase
      .from('tax_profiles')
      .select('*')
      .eq('subject_id', subjectId)
      .maybeSingle()

    const yearPromise = activeTaxYear
      ? supabase
          .from('tax_year_profiles')
          .select('*')
          .eq('subject_id', subjectId)
          .eq('tax_year', activeTaxYear)
          .maybeSingle()
      : Promise.resolve({ data: null, error: null })

    const [profileResult, yearResult] = await Promise.all([profilePromise, yearPromise])

    console.log('[useTaxProfile] profileResult:', profileResult.data, profileResult.error)
    console.log('[useTaxProfile] yearResult:', yearResult.data, yearResult.error)

    if (!profileResult.error && profileResult.data) setTaxProfile(profileResult.data)
    if (!yearResult.error   && yearResult.data)    setYearProfile(yearResult.data)

    setLoading(false)
  }, [user, subjectId, activeTaxYear])

  // ── Save stable profile fields → tax_profiles ─────────────────────────────
  const saveStableProfile = useCallback(async (updates) => {
    if (!user) return { error: 'Not authenticated' }

    // Fresh account with no subject yet — ensure a self subject exists.
    // Uses the shared, race-safe ensureSelfSubject (see useSubjects.js)
    // instead of inserting directly. The previous version here only
    // checked the local subjectId (derived from useSubjects' async load)
    // and inserted unconditionally if that hadn't resolved yet — which
    // could create a duplicate self subject if this ran before that load
    // finished, or raced against another creation path (AuthCallback.js,
    // claim-session) doing the same thing at the same time.
    let resolvedSubjectId = subjectId
    if (!resolvedSubjectId) {
      const { data: subject, error: subjectError } = await ensureSelfSubject('My Profile')
      if (subjectError || !subject) return { error: subjectError || 'Failed to create profile' }
      resolvedSubjectId = subject.id
      sessionStorage.setItem('active_subject_id', resolvedSubjectId)
      // Reload subjects so useSubjects picks up the new self subject
      reloadSubjects()
    }

    const payload = { ...updates, user_id: user.id, subject_id: resolvedSubjectId }

    const { data: existing } = await supabase
      .from('tax_profiles')
      .select('id')
      .eq('subject_id', resolvedSubjectId)
      .maybeSingle()

    let data, error
    if (existing) {
      ;({ data, error } = await supabase
        .from('tax_profiles')
        .update(payload)
        .eq('subject_id', resolvedSubjectId)
        .select()
        .maybeSingle())
    } else {
      ;({ data, error } = await supabase
        .from('tax_profiles')
        .insert(payload)
        .select()
        .maybeSingle())
    }

    if (!error && data) setTaxProfile(data)
    return { data, error, resolvedSubjectId }
  }, [user, subjectId])

  // ── Save year-specific fields → tax_year_profiles ─────────────────────────
  const saveYearProfile = useCallback(async (updates, overrideSubjectId) => {
    const effectiveSubjectId = overrideSubjectId || subjectId
    console.log('[useTaxProfile] saveYearProfile called', { user: !!user, effectiveSubjectId, activeTaxYear })
    if (!user || !effectiveSubjectId || !activeTaxYear) {
      console.log('[useTaxProfile] saveYearProfile BLOCKED — missing:', { user: !!user, effectiveSubjectId, activeTaxYear })
      return { error: 'No active tax year' }
    }

    const payload = {
      ...updates,
      user_id:    user.id,
      subject_id: effectiveSubjectId,
      tax_year:   activeTaxYear,
    }

    // Upsert on (subject_id, tax_year) unique constraint
    const { data, error } = await supabase
      .from('tax_year_profiles')
      .upsert(payload, { onConflict: 'subject_id,tax_year' })
      .select()
      .maybeSingle()

    if (!error && data) setYearProfile(data)
    return { data, error }
  }, [user, subjectId, activeTaxYear])

  // ── Combined save — called by ProfilePage ─────────────────────────────────
  const saveProfile = useCallback(async ({ stablePayload, yearPayload }) => {
    setSaving(true)

    // Run stable first so subject is created before year profile attempts
    const stableResult = await saveStableProfile(stablePayload)
    // Pass resolvedSubjectId in case this was a first-save subject creation
    const yearResult   = await saveYearProfile(yearPayload, stableResult.resolvedSubjectId)

    setSaving(false)

    // 'No active tax year' is a soft skip — stable profile saved fine
    const yearError = yearResult.error === 'No active tax year' ? null : yearResult.error
    const error = stableResult.error || yearError || null
    return { error }
  }, [saveStableProfile, saveYearProfile])

  const profileForPrompt = (taxProfile || yearProfile)
    ? buildPromptContext(taxProfile, yearProfile, activeTaxYear)
    : null

  return {
    taxProfile,
    yearProfile,
    profileForPrompt,
    loading,
    saving,
    saveProfile,
    loadProfiles,
    // Expose individual savers for any page that only needs one layer
    saveStableProfile,
    saveYearProfile,
  }
}

// ── Build system prompt context ───────────────────────────────────────────────

function getSlot(profile, slotId) {
  return profile?.profile_data?.slots?.[slotId]?.value ?? null
}

function buildPromptContext(p, y, taxYear) {
  // p = tax_profiles (stable), y = tax_year_profiles (year-specific)
  // Either may be null if not yet created

  const incomeTypes         = y?.income_types              || getSlot(p, 'income_types')             || p?.income_types  || []
  const majorEvents         = y?.major_events              || getSlot(p, 'major_events')              || p?.major_events  || []
  const retirementTypes     = y?.retirement_account_types  || getSlot(p, 'retirement_account_types')  || []
  const foreignAssetTypes   = getSlot(p, 'foreign_asset_types') || []
  const propertyOwnership   = getSlot(p, 'property_ownership')  || []
  const propertySaleDetails = y?.property_sale_details     || getSlot(p, 'property_sale_details')     || []
  const foreignBand         = y?.foreign_account_band      || p?.foreign_account_band                 || getSlot(p, 'foreign_account_band')
  const beckham             = p?.beckham_status            || getSlot(p, 'beckham_status')

  return {
    TAX_YEAR: taxYear ? String(taxYear) : 'Not specified',

    // ── Stable (tax_profiles) ──────────────────────────────────────────────
    CITIZENSHIP_STATUS:          p?.citizenship_status     || 'Not specified',
    COMUNIDAD_AUTONOMA:          getSlot(p, 'comunidad_autonoma') || 'Not specified',
    MOVE_DATE:                   p?.move_date              || getSlot(p, 'move_timing') || 'Not specified',
    BECKHAM_STATUS:              beckham                   || 'Not specified',
    MARITAL_STATUS:              getSlot(p, 'marital_status') || 'Not specified',
    ASSET_SPLIT:                 getSlot(p, 'asset_split')    || 'Not specified',
    FOREIGN_ASSET_TYPES:         Array.isArray(foreignAssetTypes) ? foreignAssetTypes.join(', ') : 'Not specified',
    PROPERTY_OWNERSHIP:          Array.isArray(propertyOwnership) ? propertyOwnership.join(', ') : 'Not specified',
    NET_SPANISH_ASSETS:          getSlot(p, 'net_spanish_assets')  || 'Not specified',
    MORTGAGE_DEBT:               getSlot(p, 'mortgage_debt')       || 'Not specified',
    WEALTH_TAX_EXPOSURE:         getSlot(p, 'wealth_tax_exposure')  || 'Not specified',

    // ── Year-specific (tax_year_profiles) — no fallback to tax_profiles ───
    // residency_year_type and spain_residency_status are year-specific;
    // falling back to tax_profiles would silently apply wrong-year residency.
    RESIDENCY_YEAR_TYPE:         y?.residency_year_type    || 'Not specified',
    SPAIN_RESIDENCY_STATUS:      y?.spain_residency_status || 'Not specified',
    DAYS_IN_SPAIN:               y?.days_in_spain          || getSlot(p, 'days_in_spain') || 'Not specified',
    COMPLEXITY_TIER:             y?.complexity_tier        || p?.complexity_tier          || 'Not assessed',
    INCOME_TYPES:                Array.isArray(incomeTypes) ? incomeTypes.join(', ') : 'Not specified',
    SELF_EMPLOYMENT_COUNTRY:     y?.self_employment_country     || getSlot(p, 'self_employment_country')        || 'Not specified',
    TOTALIZATION_STATUS:         y?.totalization_status         || getSlot(p, 'totalization_status')             || 'Not specified',
    EMPLOYMENT_LOCATION:         y?.employment_location_when_earned || getSlot(p, 'employment_location_when_earned') || 'Not specified',
    FEIE_FTC_PREFERENCE:         y?.feie_ftc_preference         || getSlot(p, 'feie_or_ftc_preference')          || 'Not specified',
    RENTAL_PROPERTY_LOCATION:    y?.rental_property_location    || getSlot(p, 'rental_property_location')        || 'Not specified',
    RENTAL_PROPERTY_SOLD:        y?.rental_property_sold != null ? String(y.rental_property_sold) : (getSlot(p, 'rental_property_sold') || 'Not specified'),
    RETIREMENT_ACCOUNT_TYPES:    Array.isArray(retirementTypes) ? retirementTypes.join(', ') : 'Not specified',
    INVESTMENT_ACCOUNT_LOCATION: y?.investment_account_location || getSlot(p, 'investment_account_location')     || 'Not specified',
    FOREIGN_ACCOUNT_BAND:        foreignBand || 'Not specified',
    MAJOR_EVENTS:                Array.isArray(majorEvents) ? majorEvents.join(', ') : 'None noted',
    PROPERTY_SALE_DETAILS:       Array.isArray(propertySaleDetails) ? propertySaleDetails.join(', ') : 'Not specified',
    FILING_GOAL:                 y?.filing_goal      || getSlot(p, 'filing_goal')       || 'Not specified',
    URGENCY_DEADLINE:            y?.urgency_deadline || getSlot(p, 'urgency_deadline')  || 'Not specified',
    PRIOR_YEAR_ISSUES:           y?.prior_year_issues || getSlot(p, 'prior_year_issues') || 'None noted',
    TRIAGE_COMPLETED:            (y?.triage_completed || p?.triage_completed) ? 'Yes' : 'No',
  }
}